import { styled, Theme } from '@mui/material/styles';
import AppsIcon from '@mui/icons-material/Apps';
import Dashboard from '@mui/icons-material/Dashboard';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import { useTheme } from '@mui/material';
import StoreMallDirectoryOutlinedIcon from '@mui/icons-material/StoreMallDirectoryOutlined';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import SyncIcon from '@mui/icons-material/Sync';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import TuneIcon from '@mui/icons-material/Tune';
import { Rule } from '@mui/icons-material';

import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { NestedList } from '../../components/NestedList';
import routes from '../../config/routes';
import { hasPermissions } from '../../helpers';
import {
  userPermissionsDataSelector,
  canUserSeeSynchroSelector,
  canUserSeeParametricStoresSelector,
} from '../../state';
import ListExternalLinkButton from './ListExternalLinkButton';

const getDashboardRoutesNav = (theme: Theme) => [
  {
    route: routes.protectedRoutes.dashboard,
    icon: <Dashboard sx={{ color: theme.palette.primary.main }} />,
    tranlationKey: 'Dashboard',
  },
];

const conciliationsRoutesNav = [
  {
    route: routes.protectedRoutes.conciliationHeader,
    icon: <StoreMallDirectoryOutlinedIcon />,
    tranlationKey: 'conciliationHeaders.title',
  },
  {
    route: routes.protectedRoutes.conciliationDetail,
    icon: <ReceiptOutlinedIcon />,
    tranlationKey: 'conciliationDetails.title',
  },
  {
    route: routes.protectedRoutes.managementReport,
    icon: <FactCheckIcon />,
    tranlationKey: 'management-report.title',
  },
];

export const LastList = styled(List)(() => ({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
}));

interface SideBarNavProps {
  isSidebarOpen: boolean;
}

const SidebarNav = ({ isSidebarOpen }: SideBarNavProps) => {
  const theme = useTheme();

  const permissionsData = useSelector(userPermissionsDataSelector);

  // Utilizamos nuestro Selector Maestro que ya tiene el soporte para _FULL
  const canSeeSynchro = useSelector(canUserSeeSynchroSelector);
  const canSeeParametricStores = useSelector(canUserSeeParametricStoresSelector);

  const permissionsStrings = useMemo(
    () => permissionsData.map(i => i.permission),
    [permissionsData]
  );

  const dashboardRoutesNav = getDashboardRoutesNav(theme);

  const dashboardRoutesAccess = useMemo(
    () => dashboardRoutesNav.filter(i => hasPermissions(permissionsStrings, i.route.permissions)),
    [permissionsStrings, dashboardRoutesNav]
  );

  const conciliationRoutesAccess = useMemo(() => {
    const baseRoutes = conciliationsRoutesNav.filter(i =>
      hasPermissions(permissionsStrings, i.route.permissions)
    );

    const groupedRoutes = [
      {
        route: routes.protectedNestedRoutes.fiscalDetail.index,
        icon: <ReceiptOutlinedIcon />,
        tranlationKey: 'fiscal-detail.title',
      },
      ...(canSeeSynchro
        ? [
            {
              route: routes.protectedRoutes.synchroForMe,
              icon: <SyncIcon />,
              tranlationKey: 'synchro4me.title',
            },
          ]
        : []),
      ...(canSeeParametricStores
        ? [
            {
              route: routes.protectedRoutes.parametricStores,
              icon: <TuneIcon />,
              tranlationKey: 'parametricStores.title',
            },
          ]
        : []),
    ];

    if (groupedRoutes.length === 0) {
      return baseRoutes;
    }

    return [
      ...baseRoutes,
      {
        route: {
          ...routes.protectedRoutes.parametricStores,
          key: 'stores-and-synchro-group',
          path: '/stores-and-synchro-group',
        },
        icon: <Rule />,
        tranlationKey: 'layout.drawer.storesAndSynchro',
        noLink: true,
        children: groupedRoutes,
      },
    ];
  }, [permissionsStrings, canSeeSynchro, canSeeParametricStores]);

  return (
    <>
      <NestedList data={dashboardRoutesAccess} />
      <Divider />
      <NestedList data={conciliationRoutesAccess} />
      <Divider />
      <LastList>
        <ListExternalLinkButton
          icon={<AppsIcon />}
          to={process.env.FLEX_TILES_URL || '/'}
          translationKey="Flex Tiles"
          showTooltip={!isSidebarOpen}
        />
      </LastList>
    </>
  );
};

export default SidebarNav;
